import { useRef } from "react";
import { motion } from "framer-motion";
import { Image as ImageIcon, Eye, Download, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CreativeItem } from "../types/creatives.types";
import { truncateProductName } from "../utils/truncateProductName";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface CreativeCardProps {
  item: CreativeItem;
  index: number;
  onClick?: () => void;
  onView?: (index: number) => void;
  onEdit?: (index: number) => void;
}

export function CreativeCard({
  item,
  index,
  onClick,
  onView,
  onEdit,
}: CreativeCardProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const { image_url, format, product_image_url } = item;
  const rawName = item.name || item.product_name || (dictionary["no-name"]?.[lang] ?? "No name");
  const name = truncateProductName(rawName);

  // Only animate on first mount — skip on re-renders / key-stable updates
  const hasRendered = useRef(false);
  const shouldAnimate = !hasRendered.current;
  hasRendered.current = true;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!image_url) return;
    try {
      const res = await fetch(image_url, { mode: "cors" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ext = image_url.split(/[#?]/)[0].match(/\.(jpe?g|png|gif|webp)$/i)?.[1] ?? "jpg";
      const filename = `${name.replace(/[^\w\s-]/g, "").replace(/\s+/g, "-") || "creative"}.${ext}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      const a = document.createElement("a");
      // a.href = image_url;
      // a.download = "image.jpg";
      a.click();
    }
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.(index);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(index);
  };

  return (
    <motion.div
      initial={shouldAnimate ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        "relative overflow-hidden rounded-xl bg-card border border-border/40",
        "transition-all duration-200 hover:shadow-md hover:border-border/60 group"
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onClick ?? (() => console.log("click creative card", index))}
        onKeyDown={(e) => e.key === "Enter" && (onClick ?? (() => {}))()}
        className="relative w-full aspect-square overflow-hidden bg-muted/20 cursor-pointer"
      >
        <div className="relative overflow-hidden h-full w-full">
          {image_url ? (
            <img
              src={image_url}
              alt={name}
              loading="lazy"
              decoding="async"
              className="opacity-100 h-full w-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
        </div>
        {/* Format badge */}
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md bg-foreground/50 backdrop-blur-sm text-[10px] font-medium text-primary-foreground">
          {format}
        </div>
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 group-hover:bg-black/40 transition-colors duration-200">
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <button
              type="button"
              onClick={handleView}
              className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
              title={dictionary["view"]?.[lang] ?? "View"}
              aria-label={dictionary["view-creative"]?.[lang] ?? "View creative"}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleEdit}
              className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
              title={dictionary["edit"]?.[lang] ?? "Edit"}
              aria-label={dictionary["edit-creative"]?.[lang] ?? "Edit creative"}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
              title={dictionary["download"]?.[lang] ?? "Download"}
              aria-label={dictionary["download-creative"]?.[lang] ?? "Download creative"}
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer with product name */}
      <div className="px-3 py-2.5 flex items-center gap-2 border-t border-border/30">
        <div className="h-5 w-5 rounded-full overflow-hidden flex-shrink-0 bg-muted/50 border border-border/30">
          {product_image_url ? (
            <img
              src={product_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <ImageIcon className="h-3 w-3 text-muted-foreground" />
            </div>
          )}
        </div>
        <span className="text-sm font-medium text-foreground truncate leading-tight">
          {name}
        </span>
      </div>
    </motion.div>
  );
}
